import { Injectable } from '@angular/core';

/**
 * Legacy parity: `FormViewFactory.formViewCache` /
 * `FormViewFactory.stateFormViewCache` (`ui/app/components/formView/formViewServices.js`).
 *
 * Acts as a process-lifetime in-memory store so that navigating away from
 * `/form-view/dashboard` (for example to `/workspace/bulk-export-history`) and
 * back does NOT reset the user's applied filter, entity tree, selected entity,
 * loaded PDF list, selected form, or rendered PDF blob.
 *
 * State is keyed by `isState` so the FED and STATE Form View screens keep
 * independent buckets, matching `FormViewCtrl`'s `vm.store` selection.
 */
export interface FormViewCacheBucket {
  /** Last applied filter dropdown selections (full objects, not just codes). */
  filters: {
    taxYear?: any;
    scenario?: any;
  };
  /** Hierarchy results keyed by `<treeType>_<bslaType>` (e.g. `FORM_`, `BSLA_ALL`). */
  entityTreeCopy: Record<string, any[]>;
  /** Currently loaded PDF tile list (after a `getTaxForms` response). */
  pdfs: any[];
  /** Currently selected entity row from the hierarchy. */
  selectedEntity: any | null;
  /** Currently selected PDF form tile. */
  selectedForm: any | null;
  /** Live blob URL for the inline iframe (regenerated when re-rendering). */
  pdfBlobUrl: string | null;
  /** Saved PDF data so we can re-create blob URLs after navigation if needed. */
  pdfBlobData: ArrayBuffer | null;
  /** Snapshot of `pdfParams` driving `/fillForm` — needed to restore after navigation. */
  pdfParams: any;
  /** Last active tree tab and bsla sub-tab. */
  treeType: string;
  bslaType: string;
  /** Whether the user has clicked "Filter" at least once in this session. */
  isFilterApplied: boolean;
  /** Open drilldown tab payloads (legacy `NewProformaFactory.tabList`). */
  drilldownTabsData: any[];
}

function emptyBucket(): FormViewCacheBucket {
  return {
    filters: {},
    entityTreeCopy: {},
    pdfs: [],
    selectedEntity: null,
    selectedForm: null,
    pdfBlobUrl: null,
    pdfBlobData: null,
    pdfParams: null,
    treeType: 'FORM',
    bslaType: '',
    isFilterApplied: false,
    drilldownTabsData: [],
  };
}

@Injectable({ providedIn: 'root' })
export class FormViewCacheService {
  private readonly formViewCache: FormViewCacheBucket = emptyBucket();
  private readonly stateFormViewCache: FormViewCacheBucket = emptyBucket();

  getBucket(isState: boolean): FormViewCacheBucket {
    return isState ? this.stateFormViewCache : this.formViewCache;
  }

  /** Persist last selected filter dropdown values. */
  saveFilters(isState: boolean, taxYear: any, scenario: any): void {
    const bucket = this.getBucket(isState);
    bucket.filters.taxYear = taxYear;
    bucket.filters.scenario = scenario;
  }

  /** Persist hierarchy tree under the `<treeType>_<bslaType>` key. */
  saveHierarchy(isState: boolean, treeType: string, bslaType: string, tree: any[]): void {
    const bucket = this.getBucket(isState);
    bucket.entityTreeCopy[`${treeType}_${bslaType}`] = tree;
    bucket.treeType = treeType;
    bucket.bslaType = bslaType;
  }

  getHierarchy(isState: boolean, treeType: string, bslaType: string): any[] | null {
    const bucket = this.getBucket(isState);
    return bucket.entityTreeCopy[`${treeType}_${bslaType}`] || null;
  }

  saveSelectedEntity(isState: boolean, entity: any): void {
    this.getBucket(isState).selectedEntity = entity;
  }

  savePdfs(isState: boolean, pdfs: any[]): void {
    this.getBucket(isState).pdfs = pdfs || [];
  }

  saveSelectedForm(isState: boolean, form: any): void {
    this.getBucket(isState).selectedForm = form;
  }

  savePdfBlob(isState: boolean, blobUrl: string | null, blobData: ArrayBuffer | null): void {
    const bucket = this.getBucket(isState);
    bucket.pdfBlobUrl = blobUrl;
    bucket.pdfBlobData = blobData;
  }

  savePdfParams(isState: boolean, pdfParams: any): void {
    // Shallow copy is enough — `pdfParams` is a flat dictionary in legacy.
    this.getBucket(isState).pdfParams = pdfParams ? { ...pdfParams } : null;
  }

  setFilterApplied(isState: boolean, applied: boolean): void {
    this.getBucket(isState).isFilterApplied = applied;
  }

  saveDrilldownTabs(isState: boolean, tabsData: any[]): void {
    this.getBucket(isState).drilldownTabsData = Array.isArray(tabsData) ? [...tabsData] : [];
  }

  getDrilldownTabs(isState: boolean): any[] {
    return this.getBucket(isState).drilldownTabsData || [];
  }

  /**
   * Called when the user explicitly clicks "Filter" with different
   * tax_year/scenario than the cached one. Drops downstream cached results
   * since they are no longer valid for the new filters.
   */
  clearDownstream(isState: boolean): void {
    const bucket = this.getBucket(isState);
    bucket.entityTreeCopy = {};
    bucket.pdfs = [];
    bucket.selectedEntity = null;
    bucket.selectedForm = null;
    bucket.pdfBlobUrl = null;
    bucket.pdfBlobData = null;
    bucket.pdfParams = null;
  }
}
